import React, {
  useEffect, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import StorageService from '../../services/StorageService';
import convertToItalianDate from '../../common/helpers/convertToItalianDate';
import {
  genders, getFiscalCode, professionalRoles,
} from '../../common/constants';
import { resetTutelaLegaleForm, tutelaLegaleGetInitialData, tutelaLegaleProfessionalSubmitAnswers } from '../../features/forms/formsActions';
import { greeniaPrivacyPolicy, modifyRequestData } from '../../services/axios-client/axeCommons';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import { FormInputRadio } from '../../common/formInputsV2/FormInputRadio';
import { FormInputText } from '../../common/formInputsV2/FormInputText';
import { FormInputSelect } from '../../common/formInputsV2/FormInputSelect';
import { FormInputDate } from '../../common/formInputsV2/FormInputDate';
import getMinAllowedBirthday from '../../common/helpers/getMinAllowedBirthday';
import FormReactSelectInputBirthPlace from '../../common/formInputsV2/FormReactSelectInputBirthPlace';
import { FormInputAddressCompleteManual } from '../../common/formInputsV2/FormInputAddressCompleteManual';
import { FormInputAddressComplete } from '../../common/formInputsV2/FormInputAddressComplete';
import getAllowedPolicyEffectDate from '../../common/helpers/getAllowedPolicyEffectDate';
import InputPrivacy from '../../common/componentsV2/inputs/InputPrivacy';
import { getBlockingFields, buildMissingFieldsMessage } from './TutelaProfessionale.stepErrors';
import { V2FontImport } from '../../common/formInputsV2/V2Fonts';
import { FormLayout } from '../../common/formInputsV2/FormLayout.styles';
import { PageHeading, PageHeadingTitle, PageHeadingSubtitle } from '../../common/formInputsV2/PageHeading.styles';
import { SurfaceCardHeading } from '../../common/formInputsV2/Card.styles';
import { FooterNav } from '../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton } from '../../common/formInputsV2/Button.styles';
import { sonnerErrorAlert } from '../../common/utils/sonnerAlerts';

function TutelaProfessionale() {
  const navigate = useNavigate();
  const location = useLocation();
  const Storage = new StorageService();
  const state = useSelector((store) => store.forms.tutela_legale);
  const [showManualAdress, setShowManualAdress] = useState(false);

  const {
    loading,
    states,
    municipalities,
    formData,
    dataPrivacy,
  } = state;

  const schema = yup.object({
    product: yup.string().required(),
    sub_product: yup.string().required(),
    source: yup.string().required(),
    name: yup
      .string()
      .nullable()
      .when('gender', ([gender], schema) => (gender !== 'G'
        ? schema
          .required('Il campo è obbligatorio. Inserisci il nome!')
          .matches(
            /^[a-zA-Z ]+$/,
            'Non sono ammessi numeri o caratteri speciali!',
          )
        : schema)),
    surname: yup
      .string()
      .nullable()
      .when('gender', ([gender], schema) => (gender !== 'G'
        ? schema
          .required('Il campo è obbligatorio. Inserisci il cognome!')
          .matches(
            /^[a-zA-Z ]+$/,
            'Non sono ammessi numeri o caratteri speciali!',
          )
        : schema)),
    date_of_birth: yup
      .string()
      .nullable()
      .when('gender', ([gender], schema) => (gender !== 'G'
        ? schema.required(
          'Il campo è obbligatorio. Seleziona data di nascita!',
        )
          .test('valid-date', 'La data di nascita non è valida', (value) => {
            if (!value) return true;
            const date = moment(value, 'YYYY-MM-DD', true);
            const year = date.year();
            const currentYear = new Date().getFullYear();

            if (year > currentYear - 18 || year < 1925) {
              return false;
            }
            return date.isValid();
          })
        : schema)),
    commune_of_birth_code: yup
      .string()
      .nullable()
      .when(['gender', 'born_abroad'], ([gender, born_abroad], schema) => (gender !== 'G'
        ? !born_abroad
          ? schema.required(
            'Il campo è obbligatorio. Inserisci luogo di nascita del propietario!',
          )
          : schema
        : schema)),
    gender: yup.string().required('Il campo è obbligatorio. Seleziona genere!'),
    phone: yup
      .string()
      .required('Il campo è obbligatorio. Inserisci numero di telefono!')
      .matches(/^[3]{1}[0-9]{8,9}$/, 'Il numero non e corretto'),
    email: yup.string()
      .required('Il campo è obbligatorio. Inserisci Email!')
      .email('Deve essere un email valido! Rimouvi spazi vuoti!')
      .test(
        'brokerEmail',
        'Non puoi impostare la tua email',
        (value) => value !== Storage.getUser().email,
      )
      .test(
        'noSymbolsBeforeAt',
        'Non sono ammessi simboli prima del carattere @',
        (value) => /^[^@]*@/.test(value),
      ),
    residence_commune_code: yup
      .string()
      .required('Il campo è obbligatorio. Seleziona provincia di residenza!'),
    province_of_birth_code: yup
      .string()
      .nullable()
      .when(['gender', 'born_abroad'], ([gender, born_abroad], schema) => (gender !== 'G'
        ? !born_abroad
          ? schema.required(
            'Il campo è obbligatorio. Inserisci luogo di nascita del propietario!',
          )
          : schema
        : schema)),
    born_abroad: yup
      .boolean()
      .required('Il campo è obbligatorio. Seleziona se nato al estero!'),
    country_of_birth_code: yup
      .string()
      .nullable()
      .when('gender', ([gender], schema) => (gender !== 'G'
        ? schema.required(
          'Il campo è obbligatorio. Seleziona il stato estero!',
        )
        : schema)),
    address: yup
      .string()
      .required('Il campo è obbligatorio. Inserisci indirizzo!'),
    house_number: yup.string().required('Il campo è obbligatorio.').matches(/^[1-9]\d*([a-zA-Z]|\/(\d+[a-zA-Z]?|[a-zA-Z]+))?$/, 'Il numero deve essere maggiore di 0.'),
    postal_code: yup
      .string()
      .required('Il campo è obbligatorio. Inserisci codice postale!')
      .matches(/^[0-9]{5}$/, 'Il codice postale non e coretto'),
    fiscal_code: yup.string().nullable(),
    residence_province_code: yup
      .string()
      .required('Il campo è obbligatorio. Seleziona provincia di residenza!'),
    existing_customer: yup.boolean(),
    active_customer_id: yup.number().nullable(),
    policy_effective_date: yup
      .date()
      .typeError('Il campo è obbligatorio. Seleziona data decorrenza!')
      .required('Il campo è obbligatorio. Seleziona data decorrenza!')
      .min(
        new Date(moment().subtract(1, 'day').format('YYYY-MM-DD')),
        'La data è nel passato',
      )
      .max(
        new Date(moment().add(30, 'day').format('YYYY-MM-DD')),
        'La data è nel futuro',
      ),
    business_name: yup
      .string()
      .nullable()
      .when('gender', {
        is: 'G',
        then: () => yup
          .string()
          .required(
            "Il campo è obbligatorio. Inserisci il nome dell'azienda!",
          ),
      }),
    vat_number: yup
      .string()
      .nullable()
      .when('gender', {
        is: 'G',
        then: () => yup
          .string()
          .max(11, 'La partiva iva non puo essere piu di 11 numeri!')
          .matches(/^[0-9]*$/, 'Solo numeri sono ammessi!')
          .required('Il campo è obbligatorio. Inserisci la tua partita iva!'),
      }),
    company_type: yup
      .string()
      .nullable()
      .when('gender', {
        is: 'G',
        then: () => yup.string().required('Il campo è obbligatorio.'),
      }),
    professional_role: yup.string().required('Il Campo e obbligatorio'),
    privacies: yup.array(yup.number()),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    getValues,
    watch,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    shouldFocusError: false,
    defaultValues: {
      product: 'tutela_legale',
      source: 'greenia.it',
      sub_product: 'tutela_professionale',
      name: '',
      surname: '',
      date_of_birth: '',
      commune_of_birth_code: null,
      gender: '',
      phone: Storage.getUser().phone ? Storage.getUser().phone : '',
      email: '',
      residence_commune_code: '',
      province_of_birth_code: null,
      born_abroad: false,
      country_of_birth_code: 'Z000',
      address: '',
      house_number: '',
      postal_code: '',
      fiscal_code: '',
      residence_province_code: '',
      existing_customer: false,
      active_customer_id: null,
      policy_effective_date: '',
      massimale: '',
      business_name: '',
      vat_number: '',
      company_type: null,
      privacies: [],
      professional_role: '',
    },
  });

  const userData = watch();

  const updateUserData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  const isValid = (name) => {
    if (errors[name]?.message !== undefined) {
      return false;
    }

    if (touchedFields[name] === undefined) {
      return false;
    }

    return true;
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.is-invalid, [aria-invalid="true"], .general-input-error, .invalid-feedback.d-block');
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  // handleSubmit's onInvalid callback — the whole form is one "step", so
  // every blocking field across it goes into a single toast instead of a
  // silent (or auto-focus-jumping) failed submit.
  const handleValidationErrors = (formErrors) => {
    const blocking = getBlockingFields(formErrors);
    sonnerErrorAlert(buildMissingFieldsMessage(blocking));
    scrollToFirstError();
  };

  const [showPrivacyErrors, setShowPrivacyErrors] = useState(false);

  const validateDataPrivacy = () => {
    let { error, message } = '';

    dataPrivacy.forEach((privacy) => {
      if (privacy.required === 1) {
        const userDataArray = { ...userData }; // make a separate copy of the object
        const index = userDataArray.privacies.indexOf(privacy.id);

        if (index < 0) {
          error = true;
          message = 'Tutti i campi sono obbligatori! Tutta la privacy con la stella deve essere controllata';
        }
      }
    });
    return { error, message };
  };

  const submitForm = (userData) => {
    const { error, message } = validateDataPrivacy();

    if (error) {
      setShowPrivacyErrors(true);
      sonnerErrorAlert(message);
      scrollToFirstError();
      return;
    }

    const _userData = { ...userData };
    if (userData.date_of_birth !== '' && userData.date_of_birth !== null) {
      _userData.date_of_birth = convertToItalianDate(userData.date_of_birth);
    } else {
      _userData.date_of_birth = null;
    }
    _userData.policy_effective_date = convertToItalianDate(
      moment(userData.policy_effective_date).format('YYYY-MM-DD'),
    );
    _userData.fiscal_code = userData.gender !== 'G' ? getFiscalCode(userData.date_of_birth, userData, municipalities, states) : null;

    if (_userData.born_abroad && (_userData.country_of_birth_code === null || _userData.country_of_birth_code === '' || _userData.country_of_birth_code == undefined)) {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Devi selezionare uno stato estero!');
      return;
    }
    tutelaLegaleProfessionalSubmitAnswers(_userData, navigate);
  };

  const privacyItemChangeHandler = (value, item) => {
    if (!value.target.checked) {
      const newArray = [...userData.privacies];
      const index = newArray.indexOf(item.id);
      newArray.splice(index, 1);
      updateUserData({
        privacies: newArray,
      });
      return;
    }
    const answersNewArray = [...userData.privacies];
    answersNewArray.push(item.id);
    updateUserData({
      privacies: answersNewArray,
    });
  };

  useEffect(() => {
    tutelaLegaleGetInitialData();

    return () => {
      resetTutelaLegaleForm();
    };
  }, []);

  // Fields that already have a real default value otherwise stay
  // neutral/grey until the user manually interacts with them — touch +
  // validate every non-empty default on mount so they show as green
  // immediately.
  useEffect(() => {
    const values = getValues();

    if (values !== undefined) {
      const objectKeys = Object.keys(values);

      objectKeys.forEach((key) => {
        if (values[key] !== '' && values[key] !== null) {
          setValue(key, values[key], {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    const callAsync = async () => {
      if (
        location.state !== null
        && location.state.requestToken !== undefined
      ) {
        const {
          data: { data: { userData } },
        } = await modifyRequestData(location.state.requestToken);

        // Only touch fields that actually have a value — touching an empty
        // field would mark it valid/green even though it's blank.
        const filledUserData = Object.fromEntries(
          Object.entries(userData).filter(
            ([, value]) => value !== '' && value !== null && value !== undefined,
          ),
        );
        updateUserData(filledUserData);
      }
    };

    callAsync();
  }, []);

  const prevGenderRef = useRef(userData.gender);

  useEffect(() => {
    const cameFromGiuridica = prevGenderRef.current === 'G';
    prevGenderRef.current = userData.gender;

    if (userData.gender === 'G') {
      updateUserData({
        born_abroad: false,
        fiscal_code: null,
        name: null,
        surname: null,
        date_of_birth: null,
        commune_of_birth_code: null,
        country_of_birth_code: null,
        province_of_birth_code: null,
      });
    } else if (cameFromGiuridica) {
      updateUserData({
        date_of_birth: '',
        country_of_birth_code: 'Z000',
        province_of_birth_code: null,
        commune_of_birth_code: null,
        born_abroad: false,
        name: '',
        surname: '',
      });
    }
  }, [userData.gender]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return <PageLoading />;
  }

  return (
    <form onSubmit={handleSubmit(submitForm, handleValidationErrors)} noValidate>
      <V2FontImport />
      <PageHeading>
        <PageHeadingTitle>Tutela Legale Professionale</PageHeadingTitle>
        <PageHeadingSubtitle>Compila i dati per generare il preventivo</PageHeadingSubtitle>
      </PageHeading>
      <div className="tutela-legale-tutorial">
        <FormLayout className="form-container">
          <FormInputRadio
            options={genders}
            label="Il contraente è"
            name="gender"
            registration={register('gender')}
            error={errors.gender?.message}
          />

          {userData.gender === 'G' ? (
            <div>
              <FormInputText
                placeholder="Ragione Sociale"
                label="Ragione Sociale"
                registration={register('business_name')}
                error={errors.business_name?.message}
                valid={isValid('business_name')}
              />
              <FormInputText
                placeholder="Partita Iva"
                label="Partita Iva"
                registration={register('vat_number')}
                error={errors.vat_number?.message}
                valid={isValid('vat_number')}
              />
              <FormInputSelect
                label="Tipologia azienda"
                registration={register('company_type')}
                error={errors.company_type?.message}
                valid={isValid('company_type')}
                placeholder="-Seleziona-"
                options={formData.company_types}
              />
            </div>
          ) : (
            <div>
              <FormInputText
                placeholder="Nome"
                label="Nome"
                registration={register('name')}
                error={errors.name?.message}
                valid={isValid('name')}
              />
              <FormInputText
                placeholder="Cognome"
                label="Cognome"
                registration={register('surname')}
                error={errors.surname?.message}
                valid={isValid('surname')}
              />
              <FormInputDate
                label="Data di nascita"
                registration={register('date_of_birth')}
                minDate={getMinAllowedBirthday()}
                maxDate={moment().subtract(18, 'years').format('YYYY-MM-DD')}
                error={errors.date_of_birth?.message}
                valid={isValid('date_of_birth')}
              />
              <FormReactSelectInputBirthPlace
                states={states}
                options={municipalities}
                label="Luogo di nascita"
                name="commune_of_birth_code"
                values={{
                  commune_of_birth_code: userData.commune_of_birth_code,
                  province_of_birth_code: userData.province_of_birth_code,
                  born_abroad: userData.born_abroad,
                  country_of_birth_code: userData.country_of_birth_code,
                }}
                onChange={(value) => updateUserData({
                  commune_of_birth_code: value.commune_of_birth_code,
                  province_of_birth_code: value.province_of_birth_code,
                  born_abroad: value.born_abroad,
                  country_of_birth_code: value.country_of_birth_code,
                })}
                valid={isValid('commune_of_birth_code')}
                error={errors.commune_of_birth_code?.message}
                valid_country_of_birth_code={isValid('country_of_birth_code')}
                error_country_of_birth_code={errors.country_of_birth_code?.message}
              />
            </div>
          )}

          {showManualAdress ? (
            <FormInputAddressCompleteManual
              municipalities={municipalities}
              formData={userData}
              isValid={isValid}
              errors={errors}
              register={register}
              updateFormData={updateUserData}
            />
          ) : (
            <FormInputAddressComplete
              municipalities={municipalities}
              formData={userData}
              isValid={isValid}
              errors={errors}
              register={register}
              updateFormData={updateUserData}
              addressId="autocomplete-address-contractor"
              setShow={setShowManualAdress}
            />
          )}

          <FormInputDate
            minDate={getAllowedPolicyEffectDate('min')}
            maxDate={getAllowedPolicyEffectDate('max')}
            registration={register('policy_effective_date')}
            label="Data di inizio della copertura della polizza"
            paragraph="Date valide: da oggi a 30 giorni da oggi"
            error={errors.policy_effective_date?.message}
            valid={isValid('policy_effective_date')}
          />

          <FormInputText
            registration={register('email')}
            label="E-mail"
            placeholder="nome@esempio.it"
            error={errors.email?.message}
            valid={isValid('email')}
          />

          <FormInputText
            registration={register('phone')}
            label="Phone"
            placeholder="Es. 3401234567"
            paragraph="Numeri di rete fissa non accettati dalle aziende"
            error={errors.phone?.message}
            valid={isValid('phone')}
          />
          <FormInputSelect
            placeholder="Ruolo professionale"
            label="Ruolo professionale"
            registration={register('professional_role')}
            error={errors.professional_role?.message}
            valid={isValid('professional_role')}
            options={professionalRoles}
          />
        </FormLayout>

        <FormLayout className="terms-container">
          <div className="terms">
            <SurfaceCardHeading>Informativa Privacy e IVASS</SurfaceCardHeading>
            <div className="text-decoration-underline mt-1 mb-2">
              <a onClick={() => greeniaPrivacyPolicy()} style={{ cursor: 'pointer' }}>
                <small>Greenia Privacy Policy</small>
              </a>
            </div>
            {dataPrivacy.map((privacy, i) => (
              <InputPrivacy
                required={privacy.required}
                label={privacy.content}
                name={privacy.type}
                id={privacy.type}
                key={i}
                checked={userData?.privacies?.includes(privacy.id)}
                hasError={showPrivacyErrors && privacy.required === 1 && !userData?.privacies?.includes(privacy.id)}
                onChange={(value) => privacyItemChangeHandler(value, privacy)}
              />
            ))}
          </div>
        </FormLayout>
      </div>

      <FooterNav className="footer-buttons button-profession-step">
        <div className="d-flex justify-content-between">
          <PrimaryButton type="submit" className="btn btn-questionnaire">
            Vai a preventivo
          </PrimaryButton>
        </div>
      </FooterNav>
    </form>
  );
}

export default TutelaProfessionale;
